// noinspection JSUnusedGlobalSymbols
require("setimmediate");

import "~utils/abortsignalPolyfill";

import React, { useEffect } from "react";
import type { AppProps, NextWebVitalsMetric } from "next/app";
import getConfig from "next/config";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { TippyProvider } from "@bloomberg/tippy";
import { GtmScript } from "@foundation/nextjs-gtm";
import { reportWebVitals as _reportWebVitals } from "@foundation/nextjs-web-vitals";
import { NotifyUnsupportedBrowser } from "@foundation/notify-unsupported-browser";
import { PersonalizationProvider } from "@foundation/personalization";
import { RegUIClientProvider } from "@foundation/reg-ui-client";
import { ScreenProvider } from "@foundation/screen";
import { useStatsigClientWebSetup, StatsigProvider } from "@foundation/statsig";

import { ANALYTICS_TEMPLATE_NAMES } from "~constants";
import { UserInfoProvider } from "~components/shared/userInfoProvider";
import { Locale } from "~constants/i18n";
import { onLocaleError } from "~i18n/errorHandler";
import type { LabelIds } from "~i18n/types";
import configureAppStore from "~redux/store";
import { isClient } from "~utils/isClient";

import "@bloomberg/intercom/src/components/styles/variables.css";
import "~styles/global.css";
import "~utils/consoleOverrides";
// IMPORTANT: This must be imported after "~styles/global.css"
import "@bloomberg/skylight/index.css";

const regUIClientCSS = require("@foundation/reg-ui-client-css");

const { publicRuntimeConfig } = getConfig();
const { s3Domain } = publicRuntimeConfig;

const PERSONALIZATION_CLIENT_URL =
    process.env.NEXT_PUBLIC_PERSONALIZATION_CLIENT_URL || "";

const STATSIG_ENV_TIER =
    process.env.NEXT_PUBLIC_COLOR_ENV === "production"
        ? "production"
        : "staging";

// https://bbgithub.dev.bloomberg.com/Consumer-Web/lineup/blob/main/doc/architecture/decisions/0004-nextjs-hydration-patch.md
const getHydratedInitialState = () => {
    if (!isClient()) {
        return {};
    }

    const initialState =
        window?.__NEXT_DATA__?.props?.pageProps?.initialState || {};

    // unless server error, don't attempt to re-hydrate from __NEXT_DATA__
    if (!initialState.serverError) {
        return {};
    }

    return initialState;
};

/**
 * DO NOT REMOVE: This function is called by NextJS framework
 */
export function reportWebVitals(metric: NextWebVitalsMetric) {
    const params = new URLSearchParams(window.location.search);
    if (params.get("inPreview") === "true") return undefined;

    return _reportWebVitals(metric);
}

/*
Add tippy dialogs here.
Ex: useTippy({ tipKey: TipKey.TIP_ZERO, variant: TipVariant.DIALOG });
 */
const TippyDialogs = () => {
    return null;
};

interface InitialState {
    pageData?: {
        preview?: boolean;
    };
    [key: string]: unknown;
}

interface PageProps {
    initialState?: InitialState;
    translatedMessages?: Record<keyof LabelIds, string>;
    locale?: Locale;
    analytics?: unknown;
}

// We are using a custom root App here to inject the global css
// used by Tailwind to all pages
export default function NextApp(props: AppProps<PageProps>) {
    const { Component, pageProps } = props;

    useEffect(() => {
        // We do not want to record page-view data for previews
        if (pageProps?.initialState?.pageData?.preview) return;

        if (!pageProps.translatedMessages) return;

        if (!window?.dataLayer) window.dataLayer = [];

        if (!pageProps.analytics) {
            window.dataLayer.push({ template: ANALYTICS_TEMPLATE_NAMES.error });
        }
    }, [pageProps]);

    const store = configureAppStore(
        pageProps.initialState || getHydratedInitialState()
    );

    const statsigClient = useStatsigClientWebSetup({
        environmentTier: STATSIG_ENV_TIER,
        statsigClientKey: process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY!,
    });

    const { translatedMessages, locale } = pageProps;

    const defaultLocale = Locale.En;
    const _locale = locale === Locale.Ja ? "ja-JP" : "en-US";

    return (
        <>
            <>
                <style
                    global
                    dangerouslySetInnerHTML={{
                        __html: regUIClientCSS,
                    }}
                />
            </>
            <Provider store={store}>
                <IntlProvider
                    locale={_locale}
                    defaultLocale={defaultLocale}
                    messages={translatedMessages}
                    onError={onLocaleError}
                >
                    <ScreenProvider>
                        <PersonalizationProvider
                            application="lineup"
                            clientUrl={PERSONALIZATION_CLIENT_URL}
                        >
                            <RegUIClientProvider>
                                <UserInfoProvider>
                                    <StatsigProvider client={statsigClient}>
                                        <TippyProvider rootSelector="[data-component=zone]">
                                            <GtmScript
                                                analytics={pageProps.analytics}
                                            />
                                            <Component {...pageProps} />
                                            {s3Domain && (
                                                <NotifyUnsupportedBrowser
                                                    s3Domain={s3Domain}
                                                />
                                            )}
                                            <TippyDialogs />
                                        </TippyProvider>
                                    </StatsigProvider>
                                </UserInfoProvider>
                            </RegUIClientProvider>
                        </PersonalizationProvider>
                    </ScreenProvider>
                </IntlProvider>
            </Provider>
        </>
    );
}
